<template>
  <div
    class="scrolled-table"
    :class="{
      'scrolled-table--scroll': hasScroll,
      'scrolled-table--start': start,
      'scrolled-table--end': end,
      'scrolled-table--fix-first': fixFirst,
      'scrolled-table--fix-last': fixLast,
      'scrolled-table--bottom-fixed': bottomFixed,
    }"
  >
    <div ref="topObserver" class="top-observer"></div>
    <div class="scrolled-table__left"></div>
    <perfect-scrollbar ref="scroll" class="scrolled-table__scroll" @scroll="scroll">
      <div ref="container" class="scrolled-table__container">
        <slot></slot>
      </div>
      <div v-if="bottomFixed" ref="bottomHide" class="bottom-shadow"></div>
    </perfect-scrollbar>
    <div class="scrolled-table__right"></div>
    <div ref="bottomObserver" class="bottom-observer"></div>
  </div>
</template>

<script>
export default {
  name: "scrolled-table",
  props: ["fixFirst", "fixLast"],

  data() {
    return {
      hasScroll: false,
      bottomAbovePageBottom: false,
      topAbovePageBottom: false,
      topFixed: false,
      start: true,
      end: false,
      rootTop: 0,
    };
  },

  computed: {
    bottomFixed() {
      return this.topAbovePageBottom && !this.bottomAbovePageBottom;
    },
  },

  mounted() {
    this._root = document.querySelector(".layout");
    this.rootTop = this._root.getBoundingClientRect().top;

    this.updateScroll();
    this.updateFix();

    this.onDispose = [];

    const resizeHandler = () => {
      this.rootTop = this._root.getBoundingClientRect().top;
    };

    window.addEventListener("resize", resizeHandler);

    const handler = () => {
      this.$nextTick(() => {
        this.updateScroll();
        this.updateFix();
      });
    };
    window.addEventListener("resize", handler);

    const layout = document.querySelector(".layout");
    layout.addEventListener("scroll", handler);

    setTimeout(() => {
      handler();
    });

    const bottomObserver = new IntersectionObserver(([entry]) => {
      this.bottomAbovePageBottom = entry.boundingClientRect.top <= entry.rootBounds.bottom;
      if (entry.isIntersecting) {
        this.$emit("nextPage");
      }
    });
    bottomObserver.observe(this.$refs.bottomObserver);

    const topObserver = new IntersectionObserver(([entry]) => {
      this.topAbovePageBottom = entry.boundingClientRect.bottom <= entry.rootBounds.bottom;
    });
    topObserver.observe(this.$refs.topObserver);

    const mutationObserver = new MutationObserver(() => {
      handler();
    });
    mutationObserver.observe(this.$refs.container, {
      childList: true,
      subtree: true,
    });

    this.onDispose.push(() => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("resize", resizeHandler);
      layout.removeEventListener("scroll", handler);
      topObserver.unobserve(this.$refs.topObserver);
      bottomObserver.unobserve(this.$refs.bottomObserver);
      mutationObserver.disconnect();
    });
  },

  beforeDestroy() {
    this.onDispose.forEach((fn) => fn());
  },

  methods: {
    scroll() {
      this.updateScroll();
    },
    updateScroll() {
      if (!this.$refs.scroll) return;
      const wrapper = this.$refs.scroll.$el;

      const { clientWidth } = wrapper;
      const { scrollWidth } = wrapper;

      if (clientWidth < scrollWidth) {
        this.hasScroll = true;

        const scrollPosition = wrapper.scrollLeft;
        this.start = scrollPosition === 0;

        this.end = clientWidth + scrollPosition >= scrollWidth - 5;
      } else {
        this.hasScroll = false;
        this.start = true;
        this.end = true;
      }
    },
    updateFix() {
      this.updateHeaderFix();
      this.updateScrollFix();
    },
    updateHeaderFix() {
      if (!this._thead) {
        this._thead = this.$el.querySelector("thead");
      }

      const thead = this._thead;
      if (!thead) return;

      const rect = this.$el.getBoundingClientRect();

      if (rect.top < this.rootTop && rect.bottom > this.rootTop) {
        this.topFixed = true;
        thead.classList.add("fixed");
        thead.style.transform = `translateY(${this.rootTop - rect.top}px)`;
        return;
      }

      this.topFixed = false;
      thead.classList.remove("fixed");
      thead.style.transform = "translateY(0)";
    },
    updateScrollFix() {
      const rect = this.$el.getBoundingClientRect();
      const height = document.documentElement.clientHeight;

      const shadow = this.$refs.bottomHide;

      if (rect.top < height && rect.bottom > height) {
        if (shadow) {
          shadow.style.transform = `translateY(${height - rect.bottom}px)`;
        }
      } else if (shadow) {
        shadow.style.transform = "translateY(0px)";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
.scrolled-table {
  font-size: 0;
  position: relative;

  .bottom-shadow {
    position: sticky;
    bottom: 0px;
    left: 0;
    right: 0;

    &:before {
      position: absolute;
      display: block;
      content: "";
      bottom: 0px;
      left: 0;
      right: 0;
      height: 30px;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      z-index: 1;
      pointer-events: none;
    }
  }

  &__scroll {
    z-index: 2;
  }

  &__container {
    display: inline-block;
    min-width: calc(100% - 1px);
    font-size: 1rem;
    padding-bottom: 12px;
  }

  & /deep/ {
    .ps__rail-x {
      opacity: 1 !important;
      background: white;

      .ps__thumb-x {
        height: 4px;
        background: $color-secondary;
        bottom: 9px;
      }

      &:hover {
        opacity: 1;

        .ps__thumb-x {
          height: 4px;
        }
      }

      @media (max-width: 992px) {
        display: none !important;
      }
    }

    thead {
      z-index: 2;

      th {
        z-index: 2;
      }

      &.fixed {
        z-index: 1;
        background: white;
        position: relative;

        box-shadow: 0 4px 10px rgba(0, 21, 80, 0.15);

        &:after {
          content: "";
          position: absolute;
          bottom: 1px;
          left: 0;
          right: 0;
          border-bottom: 1px solid #dfe4ef;
        }
      }
    }
  }

  &.scrolled-table--bottom-fixed {
    & /deep/ {
      .ps__rail-x {
        position: fixed;
        bottom: 0 !important;
        left: 230px !important;

        &:before {
          content: "";
          position: absolute;
          box-shadow: 0px 0px 15px rgba(0, 21, 80, 0.3);
          left: -30px;
          width: calc(100vw - 200px);
          bottom: 0;
          height: 25px;
          background: white;
        }
      }
    }
  }

  .app__layout_sidebar-collapsed &.scrolled-table--bottom-fixed {
    & /deep/ {
      .ps__rail-x {
        left: 90px !important;

        &:before {
          width: calc(100vw - 60px);
        }
      }
    }
  }

  &:not(.scrolled-table--scroll) {
    & /deep/ {
      .ps__rail-x {
        display: none;
      }
    }
  }

  &.scrolled-table--scroll {
    &:not(.scrolled-table--fix-first):not(.scrolled-table--start) {
      .scrolled-table__left {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0px;
        width: 20px;
        z-index: 3;
        background: linear-gradient(to left, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      }
    }

    &:not(.scrolled-table--fix-last):not(.scrolled-table--end) {
      .scrolled-table__right {
        position: absolute;
        top: 0;
        bottom: 0;
        right: -1px;
        width: 20px;
        z-index: 3;
        background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      }
    }
  }

  &.scrolled-table--fix-first {
    &:not(.scrolled-table--start) {
      .scrolled-table__scroll {
        //border-left: 1px solid #dfe4ef;
      }
    }

    & /deep/ {
      th,
      td {
        &:first-child:not(:only-child) {
          position: sticky;
          background: inherit;
          border-right: 0;
          left: 0;

          &:after {
            content: "";
            position: absolute;
            left: -1px;
            top: -1px;
            bottom: -1px;
            width: 2px;
            background: #dfe4ef;
          }
        }
      }
      th {
        &:last-child:not(:only-child) {
          border-left: none;
          // border-right: none;
        }
      }
    }

    &.scrolled-table--scroll:not(.scrolled-table--start) {
      & /deep/ {
        th,
        td {
          &:first-child:not(:only-child) {
            &:before {
              content: "";
              position: absolute;
              right: -10px;
              top: 0;
              bottom: 0;
              width: 10px;
              background: linear-gradient(to right, #001550 0%, rgba(255, 255, 255, 0) 100%);
              opacity: 0.15;
            }
          }
        }
      }
    }
  }

  &.scrolled-table--fix-last {
    &:not(.scrolled-table--end) {
      .scrolled-table__scroll {
        //border-right: 1px solid #dfe4ef;
      }
    }
    & /deep/ {
      th,
      td {
        &:last-child:not(:only-child) {
          position: sticky;
          background: inherit;
          border-left: 0;
          right: 0;

          &:after {
            content: "";
            position: absolute;
            right: -1px;
            top: -1px;
            bottom: -1px;
            width: 2px;
            background: #dfe4ef;
          }
        }
      }
      th {
        &:last-child:not(:only-child) {
          border-left: none;
        }
      }
    }

    &.scrolled-table--scroll:not(.scrolled-table--end) {
      & /deep/ {
        th,
        td {
          &:last-child:not(:only-child) {
            &:before {
              content: "";
              position: absolute;
              left: -20px;
              top: 0;
              bottom: 0;
              width: 20px;
              background: linear-gradient(to left, #001550 0%, rgba(255, 255, 255, 0) 100%);
              opacity: 0.15;
            }
          }
        }
      }
    }
  }
}
</style>
